import { useState, useEffect, useMemo } from 'react';
import { fetchDealExitLeverage } from '@/lib/api/exitLeverage';

/**
 * Hook: fetch exit leverage records for deals.
 * @param {string} tenantId
 * @param {Array} deals - Deals with deal_id or id
 * @returns Map<dealKey, ExitLeverageRecord>
 */
export function useDealExitLeverage(tenantId, deals) {
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(false);

  const dealIds = useMemo(() => {
    const ids = (deals ?? []).map((d) => getDealExitKey(d)).filter(Boolean);
    return [...new Set(ids)];
  }, [deals]);

  useEffect(() => {
    if (!tenantId || dealIds.length === 0) {
      setRecords({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    const BATCH_SIZE = 500;
    const batches = [];
    for (let i = 0; i < dealIds.length; i += BATCH_SIZE) {
      batches.push(dealIds.slice(i, i + BATCH_SIZE));
    }
    Promise.all(batches.map((batch) => fetchDealExitLeverage(tenantId, batch)))
      .then((results) => {
        if (cancelled) return;
        const merged = {};
        results.forEach((data) => {
          Object.assign(merged, data);
        });
        setRecords(merged);
      })
      .catch(() => {
        if (!cancelled) setRecords({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, dealIds.join(',')]);

  return useMemo(() => {
    const map = new Map();
    Object.entries(records).forEach(([k, v]) => {
      const rawKey = (v?.deal_id ?? k ?? '').toString().trim();
      const normKey = normalizeMapKey(rawKey);
      if (normKey) map.set(normKey, v);
      if (rawKey && rawKey !== normKey) map.set(rawKey, v);
      if (k) {
        const kNorm = normalizeMapKey(k);
        if (kNorm && !map.has(kNorm)) map.set(kNorm, v);
      }
    });
    return { map, loading };
  }, [records, loading]);
}

/** Lookup exit record for a deal - tries multiple key formats */
export function getExitRecordForDeal(map, deal) {
  if (!map || !deal) return null;
  const key = getDealExitKey(deal);
  if (!key) return null;
  const normKey = normalizeMapKey(key);
  return map.get(key) ?? map.get(normKey) ?? (() => {
    for (const v of map.values()) {
      const vId = v?.deal_id;
      if (vId === key || vId === deal?.deal_id || vId === deal?.id) return v;
      if (normalizeMapKey(vId) === normKey) return v;
    }
    return null;
  })();
}

/** Normalized deal key for lookup - use when matching against exit leverage map */
export function getDealExitKey(deal) {
  const k = deal?.deal_id ?? deal?.id ?? deal?.dealId ?? deal?._id ?? (deal?.account_name && deal?.supplier ? `${deal.account_name}|${deal.supplier}` : null);
  return k ? String(k).trim() : null;
}

/** Normalize key for map lookup - UUIDs to lowercase to handle case mismatches */
function normalizeMapKey(key) {
  if (!key) return '';
  const s = String(key).trim();
  if (/^[0-9a-fA-F-]{36}$/.test(s) || /^[0-9a-fA-F]{16,24}$/.test(s)) return s.toLowerCase();
  return s;
}

/** Check if country is US (United States) */
function isUS(country) {
  const c = (country || '').trim().toLowerCase();
  return c === 'united states' || c === 'usa' || c === 'us' || c === 'united states of america';
}

/** Check if country is Canada */
function isCanada(country) {
  const c = (country || '').trim().toLowerCase();
  return c === 'canada' || c === 'ca';
}

/**
 * Whether a deal matches the Exit leverage filter (strong | some | none | all).
 * Uses the same visibility gates as the Exit icon.
 */
export function dealMatchesExitFilter(deal, map, filter) {
  if (!filter || filter === 'all') return true;
  const record = getExitRecordForDeal(map, deal);
  const visible = Boolean(record && shouldShowExitLeverage(record, deal));
  if (filter === 'none') return !visible;
  if (!visible) return false;
  const tag = String(record.exit_tag || '').trim().toLowerCase();
  if (filter === 'strong') return tag === 'strong';
  if (filter === 'some') return tag === 'some';
  return false;
}

/**
 * Whether to show the Exit leverage icon for this deal.
 * Gates by source_family + country (e.g. E&I only for US/CA, WA_DES only for US).
 */
export function shouldShowExitLeverage(exitRow, deal) {
  if (!exitRow) return false;

  const country = (deal?.country ?? '').trim();
  const sourceFamily = (exitRow?.source_family ?? '').trim();

  if (sourceFamily === 'E&I') {
    return isUS(country) || isCanada(country);
  }
  if (sourceFamily === 'DIR') {
    return isUS(country);
  }
  if (sourceFamily === 'WA_DES') {
    return isUS(country);
  }
  if (sourceFamily === 'Equalis') {
    return isUS(country);
  }
  // Default: hide unless source family is explicitly supported
  return false;
}
